import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const API = process.env.REACT_APP_BACKEND_URL;

export const FAQPage = () => {
  const [faqSections, setFaqSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/api/content/faq`);
        const faqs = response.data;
        // Group FAQs by category
        const grouped = {};
        faqs.forEach((faq) => {
          if (!grouped[faq.category]) {
            grouped[faq.category] = [];
          }
          grouped[faq.category].push({ q: faq.question, a: faq.answer });
        });
        const sections = Object.entries(grouped).map(([category, questions]) => ({
          category,
          questions,
        }));
        setFaqSections(sections);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <div data-testid="faq-page" className="min-h-screen pt-28 pb-16 bg-cream">
      {/* Header */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mb-16">
        <div className="text-center">
          <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
            Help Center
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-warm-brown text-lg max-w-2xl mx-auto">
            Find answers to common questions about our products, orders, shipping, and more.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="max-w-4xl mx-auto px-6 md:px-12">
        {loading ? (
          <div className="space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-48 mb-4" />
                <div className="space-y-3">
                  {[1, 2].map((j) => (
                    <div key={j} className="h-14 bg-gray-100 rounded-xl" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : faqSections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-warm-brown">No FAQs available at the moment.</p>
          </div>
        ) : (
          faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-12">
              <h2 className="font-heading text-2xl font-bold text-forest mb-6 pb-2 border-b border-warm-brown/20">
                {section.category}
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {section.questions.map((faq, faqIndex) => (
                  <AccordionItem
                    key={faqIndex}
                    value={`${sectionIndex}-${faqIndex}`}
                    className="bg-sand/50 rounded-xl border border-warm-brown/10 px-6"
                  >
                    <AccordionTrigger className="text-left font-medium text-forest hover:text-gold py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-warm-brown pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))
        )}
      </section>

      {/* Contact CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 mt-16">
        <div className="bg-forest rounded-3xl p-8 md:p-12 text-center">
          <MessageCircle className="h-12 w-12 text-gold mx-auto mb-4" />
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-cream mb-4">
            Still Have Questions?
          </h2>
          <p className="text-cream/70 mb-6">
            Can't find what you're looking for? We're here to help!
          </p>
          <Link to="/contact">
            <Button className="bg-gold hover:bg-gold/90 text-forest-dark rounded-full px-8">
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
